import React, { useState, useEffect } from 'react';
import { login, register, googleAuth, User } from '../../services/authService';
import { GOOGLE_CLIENT_ID } from '../../config/api';
import './Auth.css';

interface AuthProps {
  onAuthChange: (user: User | null, token?: string) => void;
}

const Auth: React.FC<AuthProps> = ({ onAuthChange }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleInitialized, setGoogleInitialized] = useState(false);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    display_name: ''
  });

  const validateForm = (): string | null => {
    if (!formData.email || !formData.password) {
      return 'Email and password are required';
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        return 'Passwords do not match';
      }
      
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        const response = await login({
          email: formData.email,
          password: formData.password
        });
        
        if (response.success && response.data) {
          console.log('Auth successful, calling onAuthChange with:', response.data.user, response.data.token);
          onAuthChange(response.data.user, response.data.token);
        } else {
          setError(response.message || 'Login failed');
        }
      } else {
        const response = await register({
          email: formData.email,
          password: formData.password,
          username: formData.username || undefined,
          display_name: formData.display_name || undefined
        });
        
        if (response.success && response.data) {
          console.log('Registration successful, calling onAuthChange with:', response.data.user, response.data.token);
          onAuthChange(response.data.user, response.data.token);
        } else {
          setError(response.message || 'Registration failed');
        }
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSignIn = async (credential: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await googleAuth({ credential });
      
      if (response.success && response.data) {
        console.log('Google auth successful, calling onAuthChange with:', response.data.user, response.data.token);
        onAuthChange(response.data.user, response.data.token);
      } else {
        setError(response.message || 'Google authentication failed');
      }
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initialize Google Sign-In
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        console.log('🔍 Initializing Google Sign-In with client ID:', GOOGLE_CLIENT_ID);
        console.log('🔍 Current origin:', window.location.origin);
        console.log('🔍 Current href:', window.location.href);
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (response: any) => {
              console.log('🔍 Google Sign-In callback received:', response);
              if (response.credential) {
                handleGoogleSignIn(response.credential);
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            // @ts-expect-error - ux_mode is a valid property but not in the types
            ux_mode: 'popup',
            itp_support: true,
            use_fedcm_for_prompt: true
          });
          
          setGoogleInitialized(true);
          console.log('✅ Google Sign-In initialized successfully');
        } catch (error) {
          console.error('❌ Error initializing Google Sign-In:', error);
          setError('Failed to initialize Google Sign-In');
        }
      } else {
        console.log('⚠️ Google Sign-In API not available');
      }
    };

    // Load Google Sign-In script if not already loaded
    if (!window.google) {
      console.log('📥 Loading Google Sign-In script...');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Sign-In script loaded');
        setTimeout(initializeGoogleSignIn, 100); // Small delay to ensure script is fully loaded
      };
      script.onerror = () => {
        console.error('❌ Failed to load Google Sign-In script');
        setError('Failed to load Google Sign-In');
      };
      document.head.appendChild(script);
    } else {
      console.log('🔍 Google Sign-In script already loaded');
      initializeGoogleSignIn();
    }
  }, []);

  // Render Google button after initialization
  useEffect(() => {
    if (googleInitialized && googleButtonRef.current && window.google) {
      console.log('🔍 Rendering Google Sign-In button');
      try {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'continue_with',
          width: googleButtonRef.current.offsetWidth || 350,
          logo_alignment: 'left'
        });
        console.log('✅ Google Sign-In button rendered successfully');
        
        // Note: Removed auto-prompt as it may be blocked on mobile
      } catch (error) {
        console.error('❌ Error rendering Google Sign-In button:', error);
      }
    }
  }, [googleInitialized]);


  return (
    <div className="auth-container">
      <div className="auth-content">
        <p>{isLogin ? 'Sign in to your account' : 'Create a new account'}</p>
        
        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
          
          {!isLogin && (
            <>
              <div className="form-group">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  name="username"
                  placeholder="Username (optional)"
                  value={formData.username}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  name="display_name"
                  placeholder="Display name (optional)"
                  value={formData.display_name}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
            </>
          )}
          
          <button 
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="button-spinner"></span>
                {isLogin ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* Official Google Sign-In button will be rendered here */}
        <div 
          ref={googleButtonRef} 
          className="google-signin-button-container"
          style={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '40px',
            alignItems: 'center'
          }}
        >
          {!googleInitialized && (
            <span style={{ 
              color: '#666', 
              fontSize: '0.875rem'
            }}>
              Loading Google Sign-In...
            </span>
          )}
        </div>
        
        
        <div className="auth-switch">
          <button
            type="button"
            className="switch-btn"
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setFormData({
                email: '',
                password: '',
                confirmPassword: '',
                username: '',
                display_name: ''
              });
            }}
            disabled={loading}
          >
            {isLogin 
              ? "Don't have an account? Create one" 
              : 'Already have an account? Sign in'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth; 